/**
 * Greeting Agent
 * 
 * Entry point agent responsible ONLY for:
 * - Trilingual greeting (Arabic/English/Urdu)
 * - Language detection from user response
 * - Transitioning to Router Agent once language is detected
 * 
 * NOTE: System instructions and tools are defined in backend (backend/config/agents.py)
 * This file only contains tool handlers, guardrails, and metadata.
 */

import { AgentNode, AgentContext, ToolCallResult, VoiceGender } from '../types';
import { noEntryRequirements, languageDetectedGuardrail } from '../guardrails';

/**
 * Create the Greeting Agent node
 * System instructions and tools are provided by backend via ephemeral token
 */
export function createGreetingAgent(voiceGender: VoiceGender = 'male'): AgentNode {
  // Track whether user has actually spoken (prevents premature transition)
  let userHasSpoken = false;
  let pendingLanguage: 'ar' | 'en' | 'ur' | null = null;
  let activatedAt = 0;

  return {
    id: 'greeting_agent',
    name: 'Greeting Agent',
    description: 'Handles initial greeting and language detection',
    
    // Initial prompt to trigger the AI to speak the greeting first
    // Backend provides the actual system instruction via ephemeral token
    initialPrompt: 'Greet the user in 3 languages NOW and ask which language they prefer. Then WAIT for their response.',
    
    // No requirements to enter - this is the entry point
    entryGuardrails: noEntryRequirements(),
    
    // Exit when language is detected - transition to welcome agent
    exitGuardrails: {
      transitions: new Map([
        ['language_detected', {
          target: { agentId: 'welcome_agent' },
          guardrails: [languageDetectedGuardrail],
        }],
      ]),
      fallback: undefined, // No fallback - must detect language
    },

    // Called by AgentManager when user audio transcription is received
    onUserInput: () => {
      userHasSpoken = true;
      console.log('[GreetingAgent] User has spoken');
    },

    // Check if there's a pending transition (language detected but waiting for user input)
    getPendingTransition: () => {
      if (pendingLanguage && userHasSpoken) {
        const lang = pendingLanguage;
        pendingLanguage = null;
        return { language: lang, transitionTo: 'welcome_agent' };
      }
      return null;
    },
    
    // Handle the set_language_preference tool call
    handleToolCall: async (
      toolName: string,
      args: Record<string, any>,
      context: AgentContext
    ): Promise<ToolCallResult> => {
      if (toolName === 'set_language_preference') {
        const language = args.language as 'ar' | 'en' | 'ur';
        const timeSinceActivation = Date.now() - activatedAt;
        
        console.log('[GreetingAgent] Language detected:', language, 
          'userHasSpoken:', userHasSpoken, 
          'timeSinceActivation:', timeSinceActivation + 'ms');
        
        // Guard: Only trigger transition if user has actually spoken
        // AND enough time has passed (at least 3 seconds for greeting to play)
        if (userHasSpoken && timeSinceActivation > 3000) {
          console.log('[GreetingAgent] ✅ User spoke - triggering transition');
          return {
            toolName,
            args,
            result: { 
              success: true, 
              message: `Language set to ${language}` 
            },
            triggersTransition: true,
            transitionTo: 'welcome_agent',
            transitionData: { language },
          };
        } else {
          // AI called the function too early (before user spoke)
          // Store language and wait for user to actually speak.
          // Use silentResponse=true so no response.create fires and the AI stays quiet.
          console.log('[GreetingAgent] ⏳ AI called too early - storing language, waiting for user input');
          pendingLanguage = language;
          return {
            toolName,
            args,
            result: { 
              success: true, 
              message: `Language preference stored as ${language}. Stay silent and wait for user to speak.` 
            },
            triggersTransition: false,
            silentResponse: true,
          };
        }
      }
      
      // Unknown tool
      return {
        toolName,
        args,
        result: { success: false, error: 'Unknown tool' },
      };
    },
    
    onActivate: async (context: AgentContext) => {
      // Reset state on activation
      userHasSpoken = false;
      pendingLanguage = null;
      activatedAt = Date.now();
      console.log('[GreetingAgent] Activated - ready for language detection');
    },
    
    onDeactivate: async (context: AgentContext) => {
      console.log('[GreetingAgent] Deactivated - language:', context.transitionData.language);
    },
  };
}

export default createGreetingAgent;

